/**
 * LinkedIn Channel Component
 *
 * Main orchestrator for the LinkedIn channel in CRM conversations.
 * Manages state and coordinates between sidebar, chat area, and insights.
 *
 * =========================================
 * LAYOUT
 * =========================================
 *
 * Left: LinkedInSidebar (connection list)
 * Center: LinkedInChatArea (messages)
 * Right: InsightsPanel (contact details)
 *
 * =========================================
 * API INTEGRATION
 * =========================================
 *
 * To integrate with backend APIs:
 *
 * 1. Load connections:
 *    GET /conversations?channel=linkedin
 *
 * 2. Load messages:
 *    GET /conversations/:id/messages
 *
 * 3. Send message:
 *    Replace: handleSendMessage local state
 *    With: POST /api/linkedin/messages
 *
 * 4. Real-time updates:
 *    Add: WebSocket connection for new messages
 */

import React, { useCallback, useEffect, useState } from "react";
import { ChatMessage, Conversation, InsightTab } from "../../../../../views/crm-pages/conversation/types";
import { InsightsPanel } from "../../components/insight";
import {
  LinkedInConnection,
  LinkedInMessage,
  LinkedInSidebar,
  LinkedInChatArea,
} from "./components";
import useCrmApi from "../../../../hook/crm-req";

const getResponseArray = (response: any, keys: string[]): any[] => {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
    if (Array.isArray(response?.data?.[key])) return response.data[key];
  }
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const getLinkedInName = (conversation: Conversation): string =>
  conversation.title ||
  conversation.external_thread_id ||
  "LinkedIn Contact";

const toLinkedInConnection = (conversation: Conversation): LinkedInConnection => ({
  id: conversation.id,
  name: getLinkedInName(conversation),
  headline: (conversation as any).headline || (conversation as any).occupation || "LinkedIn conversation",
  avatar: (conversation as any).avatar || (conversation as any).profile_image || "",
  company: (conversation as any).company || (conversation as any).organization || "LinkedIn",
  location: (conversation as any).location || "",
  connectionDegree: ((conversation as any).connection_degree || "1st") as LinkedInConnection["connectionDegree"],
  lastMessageAt: conversation.last_message_at || conversation.created_at || new Date().toISOString(),
  lastMessage: conversation.last_message || "",
  unreadCount: conversation.unread_count || 0,
  isOnline: false,
});

const toLinkedInMessage = (message: Partial<ChatMessage> & Record<string, any>): LinkedInMessage => ({
  id: String(message.id || message.external_message_id || Date.now()),
  senderId: message.direction === "outbound" ? "me" : String(message.sender_id || "contact"),
  content: message.body || message.message || message.text || "",
  timestamp: message.created_at || message.sent_at || new Date().toISOString(),
  isOwn: message.direction === "outbound",
  status: (message.status as LinkedInMessage["status"]) || "sent",
});

/**
 * LinkedIn Channel Component
 */
export const LinkedInChannel: React.FC = () => {
  // ==========================================
  // STATE
  // ==========================================

  const [selectedConnection, setSelectedConnection] = useState<LinkedInConnection | null>(null);
  const [connections, setConnections] = useState<LinkedInConnection[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [messages, setMessages] = useState<LinkedInMessage[]>([]);
  const [activeInsightTab, setActiveInsightTab] = useState<InsightTab>("Actions");
  const [loadingLead, setLoadingLead] = useState(false);
  const token = localStorage.getItem("authToken");

  const { makeRequest: getLinkedInConversations } = useCrmApi("/conversations", "GET", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: getLinkedInMessages } = useCrmApi(
    selectedConnection?.id ? `/conversations/${selectedConnection.id}/messages` : "/conversations/messages",
    "GET",
    { Authorization: `Bearer ${token}` },
  );
  const { makeRequest: sendLinkedInMessage } = useCrmApi("/messages/send", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // ==========================================
  // HANDLERS
  // ==========================================

  const loadConnections = useCallback(async () => {
    try {
      const [response] = await getLinkedInConversations(undefined, { channel: "linkedin" });
      const conversations = getResponseArray(response, ["conversations"]) as Conversation[];
      setConnections(conversations.map(toLinkedInConnection));
    } catch (error) {
      setConnections([]);
    }
  }, [getLinkedInConversations]);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConnection) {
        setMessages([]);
        return;
      }

      setLoadingLead(true);
      try {
        const [response] = await getLinkedInMessages();
        const rawMessages = getResponseArray(response, ["messages"]) as Array<Partial<ChatMessage> & Record<string, any>>;
        setMessages(
          rawMessages
            .map(toLinkedInMessage)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        );
      } catch (error) {
        setMessages([]);
      } finally {
        setLoadingLead(false);
      }
    };

    loadMessages();
  }, [selectedConnection?.id]);

  const handleSelectConnection = (connection: LinkedInConnection) => {
    setSelectedConnection(connection);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConnection) return;

    const newMessage: LinkedInMessage = {
      id: Date.now().toString(),
      senderId: "me",
      content: messageInput,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      isOwn: true,
      status: "sent",
    };

    setMessages((prev) => [...prev, newMessage]);
    setMessageInput("");

    try {
      await sendLinkedInMessage({
        conversation_id: selectedConnection.id,
        recipient: selectedConnection.id,
        body: newMessage.content,
        type: "linkedin",
        channel: "linkedin",
      });
      loadConnections();
    } catch (error) {
      setMessages((prev) => prev.filter((message) => message.id !== newMessage.id));
    }
  };

  // ==========================================
  // INSIGHTS DATA
  // ==========================================

  // Create lead contact data for InsightsPanel
  const leadContact = selectedConnection ? {
    id: selectedConnection.id,
    first_name: selectedConnection.name.split(" ")[0],
    last_name: selectedConnection.name.split(" ").slice(1).join(" "),
    phone: "",
    email: "",
    source: "LinkedIn",
    city: selectedConnection.location.split(", ")[0] || "",
    state: selectedConnection.location.split(", ")[1] || "",
    priority: "high",
    created_at: selectedConnection.lastMessageAt,
  } : null;

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left Panel - Connection List */}
      <LinkedInSidebar
        connections={connections}
        selectedId={selectedConnection?.id || null}
        onSelect={handleSelectConnection}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Center Panel - Chat Area */}
      <LinkedInChatArea
        connection={selectedConnection}
        messages={messages}
        messageInput={messageInput}
        onMessageChange={setMessageInput}
        onSendMessage={handleSendMessage}
      />

      {/* Right Panel - Insights */}
      <InsightsPanel
        activeInsightTab={activeInsightTab}
        onTabChange={setActiveInsightTab}
        leadContact={leadContact as any}
        assignedTicket={null}
        loadingLead={loadingLead}
        team={[]}
      />
    </div>
  );
};

export default LinkedInChannel;
